import { Outlet, useLoaderData, Await } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useSubscriptionStore } from '@/features/auth/stores/subscription.store';
import { Suspense, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Sidebar } from './sidebar/Sidebar';
import { useUIStore } from '@/features/dashboard/stores/ui.store';
import { useHelpSupport } from '@/features/help/components/HelpButton';
import { useDashboardPrerequisites } from '@/features/dashboard/hooks/use-dashboard-prerequisites';
import { DashboardChecklist } from './DashboardChecklist';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { TrialEndedModal } from '@/features/auth/components/TrialEndedModal';

export const DashboardLayout = () => {
  const { logout } = useAuthStore();
  const { checkSubscription, isSubscribed, freeTrialLimit, freeUsageCount } =
    useSubscriptionStore();
  const isMenuOpen = useUIStore((state) => state.isMenuOpen);
  const showChecklistModal = useUIStore((state) => state.showChecklistModal);
  const setShowChecklistModal = useUIStore((state) => state.setShowChecklistModal);
  const { prerequisitesMet, dashboardInitialized } = useDashboardPrerequisites();
  const { openHelpSupport, HelpSupportModalComponent } = useHelpSupport();

  const loaderData = useLoaderData() as {
    appUser: any;
    coreDataPromise: Promise<any>;
  };

  useEffect(() => {
    // This effect is not directly related to the deferred loading, but we can keep it.
    // It handles the post-subscription redirect.
    const success = new URLSearchParams(window.location.search).get('success');
    if (success === 'true') {
      toast.success('Subscription activated successfully!');
      checkSubscription();
      // Clean URL
      window.history.replaceState({}, document.title, '/dashboard');
    }
  }, [checkSubscription]);

  // Show the checklist modal once initialization completes and prerequisites are missing
  useEffect(() => {
    if (dashboardInitialized) {
      setShowChecklistModal(!prerequisitesMet);
    }
  }, [dashboardInitialized, prerequisitesMet, setShowChecklistModal]);

  const handleLogout = () => {
    logout();
    window.location.href = '/login'; // Use full page reload to ensure state is cleared
  };

  // Derived flag to determine if trial has ended - only check after dashboard is initialized
  const hasTrialEnded = dashboardInitialized && !isSubscribed && freeUsageCount >= freeTrialLimit;

  return (
    <div className="flex h-screen bg-gray-50 overflow-x-hidden">
      <Sidebar onLogout={handleLogout} onHelpClick={openHelpSupport} />
      {HelpSupportModalComponent}
      {showChecklistModal && <DashboardChecklist />}
      <TrialEndedModal isOpen={hasTrialEnded} />

      {/* Main Content */}
      <main
        data-testid="dashboard-container"
        className={`
        flex-1
        transition-all duration-300 ease-in-out
        ${isMenuOpen ? 'lg:ml-64 ml-0' : 'ml-0'}
        overflow-x-hidden
        min-w-0
      `}
      >
        <Suspense fallback={<FullScreenLoader message="Loading dashboard modules..." />}>
          <Await
            resolve={loaderData.coreDataPromise}
            errorElement={
              <div className="p-6 text-center">
                <div className="text-red-500 text-6xl mb-4">⚠️</div>
                <h2 className="text-xl font-semibold text-gray-700 mb-2">
                  Dashboard Initialization Failed
                </h2>
                <p className="text-gray-500 mb-4">
                  We couldn't load the necessary data for the dashboard.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            }
          >
            {(coreData) => (
              <div className="p-6">
                <Outlet context={{ appUser: loaderData.appUser, coreData }} />
              </div>
            )}
          </Await>
        </Suspense>
      </main>
    </div>
  );
};
